/** biome-ignore-all lint/a11y/noStaticElementInteractions: Temporarily disabled, click handler on div tagß */
/** biome-ignore-all lint/a11y/useMediaCaption: Can be fixed by adding a caption to the audio element */

import type { RequestProposalResponseData } from "@albertine/types/openapi/lmt";
import { getLocale } from "albertine-shared-web";

import { type SyntheticEvent, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
    type CatalogItemPreview,
    getCatalogItemKeyDetailForLanguage,
} from "../../common/CatalogItem";
import type { ArticleInquiry } from "../../common/types/ArticleInquiry";
import type { Attachment, AttachmentWithFile } from "../../common/types/Attachment";
import {
    BookingInMessage,
    type BookingToConfirmInMemberMessage,
} from "../../common/types/Booking";
import type { CatalogArticleItem } from "../../common/types/Storyblok";
import { isTypeOf } from "../../common/utils/zod.util";
import routes from "../../utils/routes";
import { optimizeImageUrl } from "../../utils/Storyblok.image.util";
import { viewAttachment } from "../../utils/view-message-attachment.util";
import WithProposal, { type TargetedPublicBooking } from "../Bookings/WithProposal";
import ItemNotFound from "../CatalogBeta/ItemNotFound";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import Hr from "../Common/Hr";
import Icon, { PictogramIcon } from "../Common/Icon";
import FullScreenOverlay from "../Common/Overlay/FullScreenOverlay";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import MessageAttachmentPreview from "./MessageAttachmentPreview";
import "./MessageAttachmentPreviews.css";
import type { MemberDataFirestoreRecord } from "@albertine/types";
import { type CatalogItemFull, CatalogItemFullSchema } from "@albertine/types/openapi";
import type { Nomination } from "../../common/types/Referrals";
import { singularPlural } from "../../common/utils/time.formatted.util";
import { EventEmitter } from "../../EventEmitter";
import { nameWithoutTitle } from "../../utils/members.util";
import InlineTextDelimiter from "../Common/InlineTextDelimiter";
import Label from "../Common/Label";
import { SharedMiniAppEmbed } from "./SharedMiniAppEmbed";

function ProposalOverlayContent(props: {
    currentMember: MemberDataFirestoreRecord;
    requestId: string;
    template: "booking" | "proposal";
    publicProposal: RequestProposalResponseData;
    targetBooking: TargetedPublicBooking | undefined;
    targetBookingId: string;
    onEdit: () => void;
    onClose: () => void;
}) {
    const {
        currentMember,
        requestId,
        targetBooking,
        targetBookingId,
        template,
        onEdit,
        onClose,
        publicProposal,
    } = props;
    const { tenant } = currentMember;
    const locale = getLocale(currentMember.serviceLanguage);
    const showProposal = template === "proposal" || !targetBooking;

    return (
        <>
            <Flex.Row
                className="proposal-overlay__actions"
                alignItems="center"
                justifyContent="space-between"
                gap="medium"
            >
                <div>
                    <Flex.Row gap="medium">
                        <h3>{publicProposal.title}</h3>
                        {showProposal && (
                            <>
                                <InlineTextDelimiter />
                                <Label.Medium.N50>
                                    {singularPlural(
                                        `${publicProposal.bookings.length} option`,
                                        `${publicProposal.bookings.length} options`,
                                        publicProposal.bookings.length,
                                    )}
                                </Label.Medium.N50>
                            </>
                        )}
                        <InlineTextDelimiter />
                        <Label.Medium.N50>
                            {nameWithoutTitle(currentMember)}
                        </Label.Medium.N50>
                    </Flex.Row>
                </div>

                <div>
                    <Flex.Row gap="xlarge">
                        {template === "booking" && (
                            <Button.N10
                                onClick={() => {
                                    onEdit();
                                }}
                                nowrap
                            >
                                Edit
                            </Button.N10>
                        )}
                        <Button.Ghost
                            onClick={() => {
                                onClose();
                            }}
                        >
                            <Icon.Close.Medium.N10 />
                        </Button.Ghost>
                    </Flex.Row>
                </div>
            </Flex.Row>

            <Flex.Column className="proposal-overlay__content">
                <SharedMiniAppEmbed
                    path={template}
                    resourceId={template === "booking" ? targetBookingId : requestId}
                    tenant={tenant}
                    locale={locale}
                    hash={template === "proposal" ? targetBookingId : undefined}
                />
            </Flex.Column>
        </>
    );
}

type ProposalOverlayProps = {
    currentMember: MemberDataFirestoreRecord;
    requestId: string;
    template: "proposal" | "booking";
    targetBookingId: string;
    onEdit: () => void;
    onClose: () => void;
};

function ProposalOverlay(props: ProposalOverlayProps) {
    const { currentMember, targetBookingId, requestId, template, onClose, onEdit } =
        props;

    return (
        <FullScreenOverlay className="proposal-overlay">
            <WithProposal
                requestId={requestId}
                targetBookingId={targetBookingId}
                renderOnError={
                    <ItemNotFound
                        contentItemId={requestId}
                        onClose={() => {
                            onClose();
                        }}
                    />
                }
                renderLoading={
                    <Flex.Column
                        className="proposal-overlay__loading"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <LoadingSpinner />
                    </Flex.Column>
                }
            >
                {(publicProposal, targetPublicBooking) => (
                    <ProposalOverlayContent
                        currentMember={currentMember}
                        requestId={requestId}
                        template={template}
                        publicProposal={publicProposal}
                        targetBooking={targetPublicBooking}
                        targetBookingId={targetBookingId}
                        onEdit={onEdit}
                        onClose={onClose}
                    />
                )}
            </WithProposal>
        </FullScreenOverlay>
    );
}

type Props = {
    wrap?: boolean;
    currentMember: MemberDataFirestoreRecord;
    /** When set, attachments open via the secured API for this message. */
    persistedMessageDownload?: { messageId: string };
    displayBigImages?: boolean;
    displayDivider?: boolean;
    displayBookingInOverlay?: boolean;
    articleInquiry?: ArticleInquiry | undefined | null; // deprecated from app version onwards 1.4.18
    articles?: CatalogArticleItem[] | undefined | null; // deprecated but still in use for education and activation messages
    removeArticle?: ((article: CatalogArticleItem) => void) | undefined;
    articlesV2?: CatalogItemPreview[] | CatalogItemFull[] | undefined | null;
    removeArticleV2?: ((article: CatalogItemFull) => void) | undefined;
    attachments?: Attachment[] | undefined | null;
    removeAttachment?: ((attachment: AttachmentWithFile) => void) | undefined;
    request: string | undefined;
    bookingsTitle?: string | undefined | null;
    bookings?:
        | BookingInMessage[]
        | BookingToConfirmInMemberMessage[]
        | undefined
        | null;
    confirmedBooking?: BookingToConfirmInMemberMessage | undefined | null;
    nomination?: (Nomination & { message: string }) | undefined | null;
    removeBooking?: ((booking: BookingInMessage) => void) | undefined;
};

const thumbnailSize = { width: 40, height: 40 };

function handleAudioError(event: SyntheticEvent<HTMLAudioElement>) {
    const audioElement = event.currentTarget;
    const { error } = audioElement;
    if (error) {
        switch (error.code) {
            case MediaError.MEDIA_ERR_ABORTED:
                break;
            case MediaError.MEDIA_ERR_NETWORK:
                break;
            case MediaError.MEDIA_ERR_DECODE:
                console.error("An error occurred while decoding the audio.", error);
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        type: "error",
                        message: "An error occurred while decoding the audio.",
                    },
                });
                break;
            case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                console.error(
                    "The audio is missing or is in a format not supported by your browser.",
                    error,
                );
                EventEmitter.emit({
                    name: "showToastMessage",
                    params: {
                        type: "error",
                        message:
                            "The audio is missing or is in a format not supported by your browser.",
                    },
                });
                break;
            default:
                console.error("Unknown audio playback error.", error);
                break;
        }
    }
}

function MessageAttachmentPreviews(props: Props) {
    const {
        wrap,
        currentMember,
        persistedMessageDownload,
        displayBigImages,
        displayDivider,
        displayBookingInOverlay,
        articleInquiry,
        articles,
        removeArticle,
        articlesV2,
        removeArticleV2,
        attachments,
        removeAttachment,
        bookingsTitle,
        bookings,
        confirmedBooking,
        removeBooking,
        nomination,
        request,
    } = props;

    const history = useHistory();

    const [bookingOverlay, setBookingOverlay] = useState<
        BookingInMessage | BookingToConfirmInMemberMessage
    >();
    const bookingsIncludeNonConfirmedItems = bookings?.some(
        (it) => it.reservationStatus !== "confirmed",
    );

    const { url: matchUrl } = useRouteMatch();

    const memberId = useRouteMatch<{
        memberId: string;
    }>("/member/:memberId")?.params.memberId;

    const requestId = useRouteMatch<{
        requestId: string;
    }>("/request/:requestId")?.params.requestId;

    const attachmentList = attachments ?? [];

    function openMessageAttachment(attachment: Attachment) {
        if (!persistedMessageDownload) {
            void viewAttachment(attachment);
            return;
        }
        const idx = attachmentList.findIndex((a) => a === attachment);
        const base = {
            messageId: persistedMessageDownload.messageId,
            memberId: currentMember.id,
        };
        if (idx >= 0) {
            void viewAttachment(attachment, { ...base, attachmentIndex: idx });
        } else {
            void viewAttachment(attachment);
        }
    }

    function goToEditBooking(bookingId: string) {
        if (request) {
            history.push(routes.requestBooking(request, bookingId));
        }
    }

    const articleInquiryAsComponent = articleInquiry && (
        <MessageAttachmentPreview
            key={articleInquiry.article.uuid}
            title={articleInquiry.article.content.title}
            image={
                articleInquiry.article.content.coverImage?.filename
                    ? optimizeImageUrl(
                          articleInquiry.article.content.coverImage?.filename,
                          thumbnailSize,
                      )
                    : undefined
            }
            onClick={() => {
                history.push(`${matchUrl}/articles/${articleInquiry.article.uuid}`);
            }}
        />
    );

    const articlesAsComponents =
        articles &&
        articles.length > 0 &&
        articles.map((article) => {
            const imageUrl = article.content.coverImage?.filename;
            const image = imageUrl && optimizeImageUrl(imageUrl, thumbnailSize);
            return (
                <MessageAttachmentPreview
                    key={article.uuid}
                    title={article.content.title}
                    image={image}
                    onRemove={
                        removeArticle
                            ? () => {
                                  removeArticle(article);
                              }
                            : undefined
                    }
                    onClick={() => {
                        history.push(`${matchUrl}/articles/${article.uuid}`);
                    }}
                />
            );
        });

    const articlesV2asComponents =
        articlesV2 &&
        articlesV2.length > 0 &&
        articlesV2.map((article: CatalogItemPreview | CatalogItemFull) => {
            const isCatalogItemFull = isTypeOf(article, CatalogItemFullSchema);
            const { image, articleName, articleKeyDetail } = (() => {
                if (isCatalogItemFull) {
                    return {
                        image: article.images[0].url,
                        articleName: article.name,
                        articleKeyDetail:
                            getCatalogItemKeyDetailForLanguage(
                                article,
                                getLocale(currentMember.serviceLanguage),
                            ) ?? undefined,
                    };
                } else {
                    return {
                        image: article.image,
                        articleName: article.name,
                        articleKeyDetail: article.keyDetail ?? undefined,
                    };
                }
            })();

            return (
                <MessageAttachmentPreview
                    key={article.id}
                    title={articleName}
                    subtitle={articleKeyDetail}
                    image={image}
                    onRemove={
                        isCatalogItemFull && removeArticleV2
                            ? () => {
                                  removeArticleV2(article);
                              }
                            : undefined
                    }
                    onClick={() => {
                        history.push(
                            routes.catalogView(
                                "browse",
                                article.id,
                                requestId,
                                memberId,
                            ),
                        );
                    }}
                />
            );
        });

    const images = attachments?.filter((attachment) =>
        attachment.mimeType?.startsWith("image/"),
    );
    const bigImagesAsComponents =
        images && images.length > 0
            ? images.map((attachment) => (
                  <div
                      key={attachment.id}
                      className="message-attachment-preview__big-image"
                      onClick={(e) => e.currentTarget.classList.toggle("fullscreen")}
                      onKeyDown={(e) => {
                          if (
                              e.currentTarget.classList.contains("fullscreen") &&
                              e.key === "Escape"
                          ) {
                              e.currentTarget.classList.remove("fullscreen");
                          }
                      }}
                  >
                      <img
                          src={attachment?.url ?? ""}
                          alt=""
                          height={200}
                          width="auto"
                      />
                  </div>
              ))
            : undefined;

    const attachmentsAsComponents =
        attachments &&
        attachments.length > 0 &&
        attachments
            .filter((attachment) => {
                if (displayBigImages) {
                    return !attachment.mimeType?.startsWith("image/");
                }
                return true;
            })
            .map((attachment) => {
                const title = attachment.filename
                    ? attachment.filename
                    : attachment.mimeType?.startsWith("image/")
                      ? "Image"
                      : "Attachment";
                const image = attachment.mimeType?.startsWith("image/")
                    ? attachment.url
                    : undefined;
                const pictogram = !image ? (
                    <PictogramIcon.Description.Medium.S90 />
                ) : undefined;
                if (attachment.mimeType?.startsWith("audio/")) {
                    return (
                        <MessageAttachmentPreview
                            key={attachment.id}
                            onRemove={
                                removeAttachment
                                    ? () => {
                                          removeAttachment(attachment);
                                      }
                                    : undefined
                            }
                        >
                            <audio
                                controls
                                src={attachment?.url ?? ""}
                                onError={handleAudioError}
                            >
                                Your browser does not support the
                                <code>audio</code> element.
                            </audio>
                        </MessageAttachmentPreview>
                    );
                }
                return (
                    <MessageAttachmentPreview
                        key={attachment.id}
                        title={title}
                        subtitle={attachment.mimeType}
                        image={image}
                        pictogram={pictogram}
                        onRemove={
                            removeAttachment
                                ? () => {
                                      removeAttachment(attachment);
                                  }
                                : undefined
                        }
                        onClick={() => {
                            void openMessageAttachment(attachment);
                        }}
                    />
                );
            });

    const bookingsAsComponents =
        bookings && bookings.length > 0
            ? bookings?.map((booking) => {
                  const imageIcon =
                      booking.reservationStatus === "confirmed" ? (
                          <PictogramIcon.Check.Small.P90 />
                      ) : undefined;
                  return (
                      <MessageAttachmentPreview
                          key={booking.id}
                          title={booking.title}
                          image={booking.imageURL}
                          imageIcon={imageIcon}
                          onRemove={
                              removeBooking && isTypeOf(booking, BookingInMessage)
                                  ? () => {
                                        removeBooking(booking);
                                    }
                                  : undefined
                          }
                          onClick={() => {
                              if (displayBookingInOverlay) {
                                  setBookingOverlay(booking);
                                  return;
                              }
                              goToEditBooking(booking.id);
                          }}
                      />
                  );
              })
            : undefined;

    const confirmedBookingAsComponent = confirmedBooking ? (
        <>
            <MessageAttachmentPreview
                key={confirmedBooking.id}
                title={confirmedBooking.title}
                image={confirmedBooking.imageURL}
                onClick={() => {
                    if (displayBookingInOverlay) {
                        setBookingOverlay(confirmedBooking);
                        return;
                    }
                    goToEditBooking(confirmedBooking.id);
                }}
            />
            {(confirmedBooking.options || []).some(
                (option) => option.memberPreferred,
            ) && (
                <>
                    <Label.Small.N100>Preference</Label.Small.N100>
                    {confirmedBooking.options?.map((option) => {
                        if (!option.memberPreferred) return null;
                        return (
                            <li key={option.id}>
                                <Label.Medium.N100>{option.title}</Label.Medium.N100>
                            </li>
                        );
                    })}
                </>
            )}
        </>
    ) : undefined;

    const nominationAsComponent = nomination ? (
        <>
            <h4>New member nomination</h4>
            <hr />
            <Label.Medium.N100>{nomination.message}</Label.Medium.N100>
            <Label.Medium.SemiBold.N100>Name:</Label.Medium.SemiBold.N100>
            <Label.Medium.N100>
                {nomination.firstName} {nomination.lastName}
            </Label.Medium.N100>
            <Label.Medium.SemiBold.N100>Phone:</Label.Medium.SemiBold.N100>
            <Label.Medium.N100>{nomination.phoneNumber}</Label.Medium.N100>
        </>
    ) : undefined;

    const hasArticleInquiry = articleInquiry !== undefined;
    const hasArticlesV2 = articlesV2 && articlesV2.length > 0;
    const hasArticles = articles && articles.length > 0;
    const hasAttachments = attachments && attachments.length > 0;
    const hasBookings = bookings && bookings.length > 0;
    const hasAnyAttachments =
        hasArticleInquiry ||
        hasArticles ||
        hasArticlesV2 ||
        hasAttachments ||
        hasBookings ||
        confirmedBooking ||
        nomination;

    if (!hasAnyAttachments) {
        return null;
    }

    const allAttachments: React.JSX.Element[] = [
        ...(displayBigImages && bigImagesAsComponents ? bigImagesAsComponents : []),
        ...(articleInquiryAsComponent ? [articleInquiryAsComponent] : []),
        ...(articlesAsComponents || []),
        ...(articlesV2asComponents || []),
        ...(attachmentsAsComponents || []),
    ];

    const BookingsWithTitle = bookings?.length ? (
        <>
            {bookingsTitle && (
                <>
                    <h5>{bookingsTitle}</h5>
                    <Hr.XXSmall />
                </>
            )}
            {bookingsAsComponents}
        </>
    ) : null;

    return (
        <div className="message-attachment-previews">
            {wrap ? (
                <Flex.Row gap="medium" wrap="wrap">
                    {allAttachments}
                    {bookingsAsComponents}
                    {confirmedBookingAsComponent}
                </Flex.Row>
            ) : (
                <Flex.Column gap="medium">
                    {allAttachments}
                    {allAttachments.length > 0 && bookingsAsComponents && <Hr.XSmall />}
                    {BookingsWithTitle}
                    {confirmedBookingAsComponent}
                    {nominationAsComponent}
                </Flex.Column>
            )}
            {displayDivider && <Hr.Medium />}

            {bookingOverlay && request && (
                <ProposalOverlay
                    currentMember={currentMember}
                    requestId={request}
                    template={
                        bookingsIncludeNonConfirmedItems &&
                        isTypeOf(bookingOverlay, BookingInMessage)
                            ? "proposal"
                            : "booking"
                    }
                    targetBookingId={bookingOverlay.id}
                    onEdit={() => {
                        setBookingOverlay(undefined);
                        goToEditBooking(bookingOverlay.id);
                    }}
                    onClose={() => {
                        setBookingOverlay(undefined);
                    }}
                />
            )}
        </div>
    );
}

export default MessageAttachmentPreviews;
